import React, { useEffect, useState, useRef } from 'react'

interface AnimatedCounterProps {
  target: number | string
  label: string
  icon: string
  duration?: number // duración en ms
}

const AnimatedCounter: React.FC<AnimatedCounterProps> = ({ target, label, icon, duration = 2000 }) => {
  const [count, setCount] = useState(0)
  const [isVisible, setIsVisible] = useState(false)
  const elementRef = useRef<HTMLDivElement>(null)

  // Extraer número y unidad del target
  const numericTarget = typeof target === 'string' ? parseInt(target.replace(/\D/g, ''), 10) || 0 : target
  const isPercentage = typeof target === 'string' && target.includes('%')

  // Detectar unidad (m², m, etc.)
  let unit = ''
  if (typeof target === 'string') {
    if (target.includes('m²')) unit = ' m²'
    else if (target.includes('m')) unit = ' m'
    else if (target.includes('%')) unit = '%'
  }

  // Detectar cuando el elemento es visible en pantalla
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          // Dejar de observar después de que inicie la animación
          observer.unobserve(entry.target)
        }
      },
      { threshold: 0.5 }
    )

    if (elementRef.current) {
      observer.observe(elementRef.current)
    }

    return () => observer.disconnect()
  }, [])

  // Animar el contador
  useEffect(() => {
    if (!isVisible) return

    let startTime: number | null = null
    let animationFrameId: number

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp
      const progress = Math.min((timestamp - startTime) / duration, 1)

      // Easing function (ease-out)
      const easeOut = 1 - Math.pow(1 - progress, 3)

      // Calcular valor actual
      const currentCount = Math.floor(easeOut * numericTarget)
      setCount(currentCount)

      if (progress < 1) {
        animationFrameId = requestAnimationFrame(animate)
      }
    }

    animationFrameId = requestAnimationFrame(animate)

    return () => cancelAnimationFrame(animationFrameId)
  }, [isVisible, numericTarget, duration])

  // Formatear número con separador de miles
  const formattedCount = count.toLocaleString('es-CL')

  return (
    <div ref={elementRef} className="animate-slide-up">
      <div className="text-2xl sm:text-3xl md:text-4xl mb-1 sm:mb-2">{icon}</div>
      <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-1 sm:mb-2">
        {formattedCount}{unit}
      </div>
      <div className="text-xs sm:text-sm md:text-base text-gray-600 font-medium">{label}</div>
    </div>
  )
}

export default AnimatedCounter
